/*
 * Canonical CTA click-tracking core.
 *
 * Consumed by:
 *   - theme/assets/audible-tracking.js  (audible_click)
 *   - theme/assets/mission-tracking.js  (mission_click)
 *
 * Owns the shared chain: install-once guard, userCanBeTracked() consent gate
 * per rules/cookie_consent_convention.md, pointerdown/click/auxclick capture
 * with button=0|1 filter, 500ms dedupe, and 3-sink publish (Shopify.analytics
 * + dataLayer + gtag).
 *
 * Per rules/canonical_centralization.md — any third sibling CTA tracker MUST
 * route through here rather than re-implementing the chain.
 *
 * Public API:
 *   window.NCCtaTracking.install({
 *     attr:            DOM attribute selector token, e.g., 'data-audible-cta'
 *     eventName:       Analytics event name, e.g., 'audible_click'
 *     idempotencyFlag: window flag name, e.g., '__ncAudibleTrackingLoaded'
 *     logPrefix:       optional diagnostic label for designMode warnings
 *     dedupeKeyFn:     optional (el) => string — dedupe key suffix
 *     extraPayload:    optional (el) => object — extra fields merged into payload
 *   })
 *
 * pointerdown is the primary trigger so middle-click / Cmd+click /
 * right-click → "Open in new tab" are captured before navigation.
 * Right-click (button=2) is filtered out to avoid context-menu false
 * positives. Click and auxclick are fallbacks for keyboard activation
 * and middle-button auxclick. A 500 ms dedupe per key suppresses the
 * synthetic click that follows pointerdown.
 *
 * Consent: when window.Shopify.customerPrivacy is exposed (GDPR/CCPA
 * banners enabled in Admin), publish only if userCanBeTracked() is true.
 * When the API is absent (most stores), fall through and publish.
 */
(function () {
  'use strict';

  if (window.NCCtaTracking) return;

  var DEDUPE_MS = 500;

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function userCanBeTracked() {
    // Two cases:
    //   1. customerPrivacy API absent → no consent banner configured →
    //      tracking is the storefront default. Return true.
    //   2. API present → respect its decision. If userCanBeTracked() throws,
    //      treat as opt-out (return false) — safer for GDPR/CCPA compliance
    //      than fail-open.
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.userCanBeTracked !== 'function') return true;
    try {
      return cp.userCanBeTracked();
    } catch (e) {
      return false;
    }
  }

  function getPageTemplate() {
    var body = document.body;
    if (body && body.className) {
      var match = body.className.match(/\btemplate-([\w-]+)\b/);
      if (match) return match[1];
    }
    if (window.Shopify && window.Shopify.template) return window.Shopify.template;
    return null;
  }

  function basePayload(el, attr) {
    return {
      cta_variant: el.getAttribute(attr),
      destination_url: el.getAttribute('href') || null,
      page_template: getPageTemplate(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || null,
      timestamp: new Date().toISOString(),
    };
  }

  // Returns true if the event was published (consent granted), false if the
  // consent gate suppressed it. Callers that need to know whether a consented
  // publish actually happened (e.g. experiment-core.js's emitView once-per-session guard)
  // read this boolean; the CTA install() path ignores it.
  function publish(eventName, payload, logPrefix) {
    if (!userCanBeTracked()) return false;

    try {
      if (window.Shopify && window.Shopify.analytics
          && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) {
      if (isDesignMode()) console.warn('[' + logPrefix + '] Shopify.analytics.publish failed', e);
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch (e) {
      if (isDesignMode()) console.warn('[' + logPrefix + '] dataLayer.push failed', e);
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
    } catch (e) {
      if (isDesignMode()) console.warn('[' + logPrefix + '] gtag failed', e);
    }

    return true;
  }

  function install(opts) {
    if (!opts || !opts.attr || !opts.eventName || !opts.idempotencyFlag) {
      throw new Error('NCCtaTracking.install: attr, eventName, idempotencyFlag required');
    }
    // Idempotent installation — survives accidental double-include (PageFly
    // wraps, theme editor reloads, etc.) without doubling counts.
    if (window[opts.idempotencyFlag]) return;
    window[opts.idempotencyFlag] = true;

    var attr = opts.attr;
    var eventName = opts.eventName;
    var logPrefix = opts.logPrefix || eventName;
    var dedupeKeyFn = opts.dedupeKeyFn || function (el) {
      return el.getAttribute(attr) + '|' + (el.getAttribute('data-product-id') || '');
    };
    var extraPayload = opts.extraPayload || function () { return {}; };
    var lastFire = { key: null, ts: 0 };

    function handle(event) {
      // Right-click context-menu (button=2) and back/forward mouse buttons
      // (button=3,4) fire pointerdown / auxclick without a follow-up
      // navigation. Filter both event types to primary (0) and middle (1)
      // only. The plain `click` event is gated by the browser to button=0
      // for left-click and keyboard activation, so it doesn't need filtering.
      if ((event.type === 'pointerdown' || event.type === 'auxclick') &&
          event.button !== 0 && event.button !== 1) return;

      var target = event.target;
      var el = target && target.closest && target.closest('[' + attr + ']');
      if (!el) return;

      var variant = el.getAttribute(attr);
      if (!variant) return;

      var key = dedupeKeyFn(el);
      var now = Date.now();
      if (lastFire.key === key && now - lastFire.ts < DEDUPE_MS) return;
      lastFire = { key: key, ts: now };

      var payload = basePayload(el, attr);
      var extras = extraPayload(el) || {};
      for (var k in extras) {
        if (Object.prototype.hasOwnProperty.call(extras, k)) {
          payload[k] = extras[k];
        }
      }

      publish(eventName, payload, logPrefix);
    }

    document.addEventListener('pointerdown', handle, true);
    document.addEventListener('click', handle, true);
    document.addEventListener('auxclick', handle, true);
  }

  // publish is exported so a page-load event (e.g. pdp_experiment_view) can be
  // emitted through this canonical consent-gated 3-sink chain WITHOUT re-inlining
  // it (rules/canonical_centralization.md). It returns a boolean so the caller
  // knows whether the consent gate let it through.
  window.NCCtaTracking = { install: install, publish: publish };
})();
